import { useState } from 'react';
import { FundraisingFund, FUND_STAGES, FUND_PRIORITIES } from '../../types';
import { Button, Input, Select, Textarea } from "@repo/o37-ui";
import { X } from "lucide-react";

const API_URL = import.meta.env.DEV ? "http://localhost:3001/api" : "/api";

interface FundModalProps {
  fundraiseId: string;
  fund?: FundraisingFund | null;
  onClose: () => void;
  onSaved: () => void;
}

export default function FundModal({ fundraiseId, fund, onClose, onSaved }: FundModalProps) {
  const isEditing = !!fund;
  
  const [name, setName] = useState(fund?.name || '');
  const [website, setWebsite] = useState(fund?.website || '');
  const [logoUrl, setLogoUrl] = useState(fund?.logoUrl || '');
  const [thesis, setThesis] = useState(fund?.thesis || '');
  const [typicalTicketMin, setTypicalTicketMin] = useState(fund?.typicalTicketMin?.toString() || '');
  const [typicalTicketMax, setTypicalTicketMax] = useState(fund?.typicalTicketMax?.toString() || '');
  const [portfolioCompanies, setPortfolioCompanies] = useState(fund?.portfolioCompanies?.join(', ') || '');
  const [stage, setStage] = useState(fund?.stage || 'outreach');
  const [priority, setPriority] = useState(fund?.priority || 'tier_2');
  const [notes, setNotes] = useState(fund?.notes || '');
  
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const userId = localStorage.getItem('app_user_id') || '';
  const userRole = localStorage.getItem('app_role') || 'user';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      setError('Fund name is required');
      return;
    }

    setSaving(true);
    setError('');

    try {
      const payload = {
        name: name.trim(),
        website: website.trim() || null,
        logoUrl: logoUrl.trim() || null,
        thesis: thesis.trim() || null,
        typicalTicketMin: typicalTicketMin ? parseFloat(typicalTicketMin) : null,
        typicalTicketMax: typicalTicketMax ? parseFloat(typicalTicketMax) : null,
        portfolioCompanies: portfolioCompanies ? portfolioCompanies.split(',').map(s => s.trim()).filter(Boolean) : null,
        stage,
        priority,
        notes: notes.trim() || null
      };

      const url = isEditing
        ? `${API_URL}/fundraising/raises/${fundraiseId}/funds/${fund.id}`
        : `${API_URL}/fundraising/raises/${fundraiseId}/funds`;

      const response = await fetch(url, {
        method: isEditing ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-user-id': userId,
          'x-user-role': userRole
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        onSaved();
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to save fund');
      }
    } catch (e) {
      setError('Failed to save fund');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-2xl shadow-xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-neutral-100">
          <h2 className="text-xl font-semibold">
            {isEditing ? 'Edit Fund' : 'Add Fund'}
          </h2>
          <button 
            onClick={onClose}
            className="p-2 hover:bg-neutral-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-neutral-500" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {error && (
            <div className="p-3 bg-red-50 text-red-600 text-sm rounded-lg border border-red-200">
              {error}
            </div>
          )}

          {/* Basic Info */}
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className="block text-sm font-medium text-neutral-700 mb-1">
                Fund Name *
              </label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g., Andreessen Horowitz"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">
                Website
              </label>
              <Input
                value={website}
                onChange={(e) => setWebsite(e.target.value)}
                placeholder="https://..."
                type="url"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">
                Logo URL
              </label>
              <Input
                value={logoUrl}
                onChange={(e) => setLogoUrl(e.target.value)}
                placeholder="https://..."
                type="url"
              />
            </div>
          </div>

          {/* Pipeline Status */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">
                Stage
              </label>
              <Select value={stage} onChange={(e) => setStage(e.target.value as any)}>
                {FUND_STAGES.map(({ stage, label }) => (
                  <option key={stage} value={stage}>{label}</option>
                ))}
                <option value="uninterested">Uninterested</option>
              </Select>
            </div>

            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">
                Priority Tier
              </label>
              <Select value={priority} onChange={(e) => setPriority(e.target.value as any)}>
                {FUND_PRIORITIES.map(({ priority, label }) => (
                  <option key={priority} value={priority}>{label}</option>
                ))}
              </Select>
            </div>
          </div>

          {/* Investment Details */}
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">
              Typical Ticket Size
            </label>
            <div className="grid grid-cols-2 gap-4">
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400">$</span>
                <Input
                  value={typicalTicketMin}
                  onChange={(e) => setTypicalTicketMin(e.target.value)}
                  placeholder="Min (e.g., 5000000)"
                  type="number"
                  className="pl-7"
                />
              </div>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400">$</span>
                <Input
                  value={typicalTicketMax}
                  onChange={(e) => setTypicalTicketMax(e.target.value)}
                  placeholder="Max (e.g., 15000000)"
                  type="number"
                  className="pl-7"
                />
              </div>
            </div>
          </div>

          {/* Details */}
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">
              Investment Thesis
            </label>
            <Textarea
              value={thesis}
              onChange={(e) => setThesis(e.target.value)}
              placeholder="What does this fund invest in? Focus areas, stage preferences..."
              rows={3}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">
              Notable Portfolio Companies
            </label>
            <Input
              value={portfolioCompanies}
              onChange={(e) => setPortfolioCompanies(e.target.value)}
              placeholder="Company A, Company B, Company C..."
            />
            <p className="text-xs text-neutral-500 mt-1">Separate with commas</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">
              Notes
            </label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Internal notes about this fund..."
              rows={3}
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-neutral-100">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={saving}
              text="Cancel"
            />
            <Button
              type="submit"
              disabled={saving}
              text={saving ? 'Saving...' : isEditing ? 'Save Changes' : 'Add Fund'}
            />
          </div>
        </form>
      </div>
    </div>
  );
}
